<template>
  <div>
    <vue-headful :title="title" />
    
    <section id="valores" class="nic-universe">
      <!-- Background Elements -->
      <div class="background-elements">
        <div class="gradient-orb orb-1"></div>
        <div class="gradient-orb orb-2"></div>
        <div class="hexagon-grid"></div>
        <div class="floating-nodes">
          <div v-for="i in 10" :key="i" class="node" :style="getNodeStyle(i)"></div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="content-layer">
        <v-container>
          <!-- Hero Section -->
          <v-row justify="center" class="hero-section">
            <v-col cols="12" md="10" lg="8" class="text-center">
              <div class="binary-text">01001110 01001001 01000011</div>
              
              <div class="title-container">
                <div class="logo-text mb-2">nic.ni</div>
                <h1 class="text-h2 text-md-h1 font-weight-black main-title">
                  <span class="title-line line-1">QUE DEFINEN</span>
                  <span class="title-line line-2 nic-gradient">.NI</span>
                </h1>
                <div class="scan-line"></div>
              </div>
              
              <div class="typing-container">
                <p class="text-subtitle-1 text-md-h6 neon-text">
                  <span class="typing-text">{{ typingText }}</span>
                  <span class="cursor" :class="{ 'typing': !typingComplete }">|</span>
                </p>
              </div>
            </v-col>
          </v-row>

          <!-- Separator Spacing -->
          <div class="section-spacer"></div>

          <!-- Interactive Orbital System -->
          <div class="orbital-section">
         
            
            <div class="orbital-container">
              <div class="orbital-system">
                <!-- Center Core -->
                <div class="core" @click="resetOrbits">
                  <div class="core-inner">
                    <v-icon x-large color="white">mdi-dna</v-icon>
                    <div class="core-pulse"></div>
                  </div>
                  <div class="core-label">CORE NIC.NI</div>
                </div>

                <!-- Orbiting Values -->
                <div 
                  v-for="(item, i) in items1" 
                  :key="`orbit-${i}`"
                  class="orbital-value"
                  :style="getOrbitStyle(i)"
                  @mouseenter="activateOrbit(i)"
                  @click="selectValue(item)"
                >
                  <div 
                    class="orbit-item"
                    :class="{ 'active': activeOrbit === i }"
                    :style="`--orbit-color: ${getColorCode(item.color)}`"
                  >
                    <div class="orbit-glow"></div>
                    
                    <div class="orb-content">
                      <div class="orb-icon" :style="`color: ${getColorCode(item.color)}`">
                        <v-icon large>{{ item.icon }}</v-icon>
                      </div>
                      
                      <div class="orb-label">{{ item.titulo }}</div>
                      
                      <!-- Tooltip on Hover -->
                      <transition name="fade">
                        <div v-if="activeOrbit === i" class="orb-tooltip">
                          <h3 class="orb-title">{{ item.titulo }}</h3>
                          <p class="orb-description">{{ item.subtitulo }}</p>
                          <div class="orb-indicator">
                            <div class="indicator-dot"></div>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>
                </div>

                <!-- Connection Lines -->
                <svg class="connection-lines" width="100%" height="100%">
                  <line 
                    v-for="i in items1.length" 
                    :key="`line-${i}`"
                    :x1="getLineX(i-1, true)" 
                    :y1="getLineY(i-1, true)"
                    :x2="getLineX(i-1, false)" 
                    :y2="getLineY(i-1, false)"
                    class="connection-line"
                    :class="{ 'active': activeOrbit === i-1 }"
                  />
                </svg>
              </div>
            </div>
          </div>

          <!-- Value Details Modal -->
          <v-dialog v-model="valueDialog" max-width="800" content-class="value-modal">
            <v-card v-if="selectedValue" class="modal-card">
              <div class="modal-header" :style="`background: linear-gradient(135deg, ${getColorCode(selectedValue.color)}20, transparent)`">
                <div class="modal-icon" :style="`background: ${getColorCode(selectedValue.color)}`">
                  <v-icon x-large color="white">{{ selectedValue.icon }}</v-icon>
                </div>
                <div class="modal-title">
                  <h2 class="text-h3 font-weight-bold">{{ selectedValue.titulo }}</h2>
                  <div class="tech-tags">
                    <span class="tech-tag" v-for="(tag, idx) in ['DNS', 'Infraestructura', 'SLA 99.9%']" :key="idx">
                      {{ tag }}
                    </span>
                  </div>
                </div>
                <v-btn icon @click="valueDialog = false" class="close-btn">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
              
              <v-card-text class="modal-content">
                <div class="description-section">
                  <p class="text-h6">{{ selectedValue.subtitulo }}</p>
                  
                  <div class="implementation-example">
                    <div class="code-preview">
                      <div class="code-header">
                        <div class="code-dots">
                          <span class="code-dot red"></span>
                          <span class="code-dot yellow"></span>
                          <span class="code-dot green"></span>
                        </div>
                        <span class="code-title">protocol.js</span>
                      </div>
                      <pre><code>
// {{ selectedValue.titulo }} Implementation
class {{ selectedValue.titulo.replace(/\s/g, '') }}Protocol {
  constructor() {
    this.domain = ".ni";
    this.uptime = "100%";
    this.security = "Tier IV";
  }
  
  ensureCompliance() {
    return "Ensuring " + this.domain + " reliability";
  }
  
  // Network Infrastructure Code
  monitorNetwork() {
    return {
      status: "optimal",
      responseTime: "&lt;2ms",
      securityLevel: "maximum"
    };
  }
}
                      </code></pre>
                    </div>
                  </div>
                </div>
                
                <v-divider class="my-6"></v-divider>
                
                <div class="metrics-section">
                  <h3 class="text-h5 mb-4">Métricas Clave</h3>
                  <v-row>
                    <v-col cols="12" md="4" v-for="metric in metrics" :key="metric.label">
                      <div class="metric-card">
                        <div class="metric-value">{{ metric.value }}</div>
                        <div class="metric-label">{{ metric.label }}</div>
                        <div class="metric-bar">
                          <div class="metric-fill" :style="`width: ${metric.percentage}%; background: ${getColorCode(selectedValue.color)}`"></div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
              
              <v-card-actions class="modal-actions">
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="valueDialog = false">
                  <v-icon left>mdi-check</v-icon>
                  Entendido
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Stats Section -->
          <div class="stats-section">
            <div class="stats-container">
              <div 
                v-for="(stat, i) in stats" 
                :key="`stat-${i}`"
                class="stat-card"
                @mouseenter="activeStat = i"
                @mouseleave="activeStat = null"
                :class="{ 'active': activeStat === i }"
              >
                <div class="stat-content">
                  <div class="stat-value" :style="`color: ${stat.color}`">{{ stat.value }}</div>
                  <div class="stat-label">{{ stat.label }}</div>
                  <div class="stat-glow" :style="`background: ${stat.color}`"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Interactive Terminal -->
          <div class="terminal-section">
            <div class="terminal-wrapper">
              <div class="terminal-container">
                <div class="terminal-header">
                  <div class="terminal-dots">
                    <span class="dot red"></span>
                    <span class="dot yellow"></span>
                    <span class="dot green"></span>
                  </div>
                  <span class="terminal-title">nic@values:~</span>
                </div>
                
                <div class="terminal-body">
                  <div 
                    v-for="(line, i) in terminalLines" 
                    :key="`line-${i}`"
                    class="terminal-line"
                  >
                    <span class="prompt">nic@values:~$</span>
                    <span class="command">{{ line.command }}</span>
                    <div class="output">{{ line.output }}</div>
                  </div>
                  
                  <div class="terminal-input">
                    <span class="prompt">nic@values:~$</span>
                    <input 
                      ref="terminalInput"
                      type="text" 
                      v-model="terminalInput"
                      @keyup.enter="executeCommand"
                      placeholder="Type 'help' for commands"
                      class="terminal-field"
                    />
                  </div>
                </div>
              </div>
              
              <div class="terminal-help">
                <h4>Comandos disponibles:</h4>
                <div class="commands-list">
                  <span class="command-item">help</span>
                  <span class="command-item">list-values</span>
                  <span class="command-item">uptime</span>
                  <span class="command-item">contact</span>
                  <span class="command-item">clear</span>
                </div>
              </div>
            </div>
          </div>

          <!-- CTA Section -->
          <div class="cta-section">
            <v-row justify="center">
              <v-col cols="12" md="8" class="text-center">
                
                <p class="text-caption mt-4 grey--text lighten-2">
                  Únete a la red más confiable de Nicaragua
                </p>
              </v-col>
            </v-row>
          </div>
        </v-container>
      </div>
    </section>
  </div>
</template>

<script>
import vueHeadful from "vue-headful";

export default {
  name: "ValoresPage",
  components: {
    vueHeadful,
  },
  data: () => ({
    title: "Nuestros Valores | NIC .NI",
    typingText: "",
    typingIndex: 0,
    typingComplete: false,
    activeOrbit: null,
    selectedValue: null,
    valueDialog: false,
    activeStat: null,
    terminalInput: "",
    terminalLines: [
      { command: "list-values", output: "[Responsabilidad, Compromiso, Sinergia, Innovación, Integridad]" },
      { command: "ping nic.ni", output: "64 bytes from nic.ni: time=2ms TTL=64" },
      { command: "uptime", output: "100% uptime since 1991" }
    ],
    stats: [
      { value: "100%", label: "Uptime", color: "#00BCD4" },
      { value: "100%", label: "Personal Capacitado", color: "#9C27B0" },
      { value: "∞", label: "Escalabilidad", color: "#FF4757" },
      { value: "0", label: "Incidentes", color: "#4CAF50" }
    ],
    metrics: [
      { label: "Confiabilidad", value: "99.99%", percentage: 100 },
      { label: "Seguridad", value: "Tier IV", percentage: 95 },
      { label: "Performance", value: "Óptimo", percentage: 98 }
    ],
    items1: [
      {
        color: "red",
        icon: "mdi-shield-lock",
        titulo: "RESPONSABILIDAD",
        subtitulo: "Infraestructura con redundancia multi-nivel y protocolos de seguridad de última generación.",
      },
      {
        color: "purple",
        icon: "mdi-rocket-launch",
        titulo: "COMPROMISO",
        subtitulo: "Impulsamos la transformación digital con soluciones escalables y soporte técnico especializado.",
      },
      {
        color: "teal",
        icon: "mdi-brain",
        titulo: "SINERGIA",
        subtitulo: "Conectamos personas, empresas y tecnología para crear ecosistemas digitales colaborativos.",
      },
      {
        color: "indigo",
        icon: "mdi-chip",
        titulo: "INNOVACIÓN",
        subtitulo: "Implementamos hoy las tecnologías del mañana, manteniendo .NI a la vanguardia regional.",
      },
      {
        color: "orange",
        icon: "mdi-key",
        titulo: "INTEGRIDAD",
        subtitulo: "Transparencia total y gestión ética de datos, pilares fundamentales del ecosistema .NI.",
      },
    ],
  }),
  mounted() {
    this.typeText();
    setTimeout(() => {
      if (this.$refs.terminalInput) {
        this.$refs.terminalInput.focus();
      }
    }, 1000);
  },
  methods: {
    getColorCode(colorName) {
      const colorMap = {
        red: "#FF4757",
        purple: "#9C27B0",
        teal: "#00BCD4",
        indigo: "#3D5AFE",
        orange: "#FF9800"
      };
      return colorMap[colorName] || colorName;
    },
    
    typeText() {
      const fullText = "Los principios fundamentales de nuestra infraestructura digital";
      if (this.typingIndex < fullText.length) {
        this.typingText += fullText.charAt(this.typingIndex);
        this.typingIndex++;
        setTimeout(this.typeText, 50);
      } else {
        this.typingComplete = true;
      }
    },
    
    getNodeStyle(i) {
      const angle = (i / 10) * Math.PI * 2;
      const radius = 200 + Math.random() * 300;
      const size = 5 + Math.random() * 10;
      return {
        left: `calc(50% + ${Math.cos(angle) * radius}px)`,
        top: `calc(50% + ${Math.sin(angle) * radius}px)`,
        width: `${size}px`,
        height: `${size}px`,
        animationDelay: `${i * 0.3}s`,
        opacity: 0.2 + Math.random() * 0.3
      };
    },
    
    getOrbitStyle(i) {
      const angle = (i / this.items1.length) * Math.PI * 2;
      const radius = 320;
      return {
        '--orbit-angle': `${angle}rad`,
        '--orbit-radius': `${radius}px`,
        '--orbit-index': i
      };
    },
    
    getLineX(index, isStart = false) {
      const angle = (index / this.items1.length) * Math.PI * 2;
      const centerX = 50; // Porcentaje
      const radius = isStart ? 0 : 16; // Radio ajustado
      return centerX + Math.cos(angle) * radius;
    },
    
    getLineY(index, isStart = false) {
      const angle = (index / this.items1.length) * Math.PI * 2;
      const centerY = 50;
      const radius = isStart ? 0 : 16;
      return centerY + Math.sin(angle) * radius;
    },
    
    activateOrbit(index) {
      this.activeOrbit = index;
    },
    
    selectValue(item) {
      this.selectedValue = item;
      this.valueDialog = true;
      this.activeOrbit = this.items1.indexOf(item);
    },
    
    resetOrbits() {
      this.activeOrbit = null;
      this.valueDialog = false;
    },
    
    executeCommand() {
      const command = this.terminalInput.trim().toLowerCase();
      
      if (command === 'help') {
        this.terminalLines.push({
          command: this.terminalInput,
          output: "Available commands: list-values, ping, uptime, contact, clear"
        });
      } else if (command === 'clear') {
        this.terminalLines = [];
      } else if (command === 'contact') {
        this.terminalLines.push({
          command: this.terminalInput,
          output: "Redirecting to contact form..."
        });
        setTimeout(() => {
          this.$router.push('/contacto');
        }, 1000);
      } else if (command === 'list-values') {
        this.terminalLines.push({
          command: this.terminalInput,
          output: this.items1.map(item => item.titulo).join(', ')
        });
      } else if (command === 'uptime') {
        this.terminalLines.push({
          command: this.terminalInput,
          output: "System uptime: 100% (since 1991)"
        });
      } else if (command === 'ping') {
        this.terminalLines.push({
          command: this.terminalInput,
          output: "PING nic.ni (200.10.150.1): 56 data bytes\n64 bytes from 200.10.150.1: icmp_seq=0 ttl=64 time=1.8 ms"
        });
      } else if (command) {
        this.terminalLines.push({
          command: this.terminalInput,
          output: "Command not found. Type 'help' for available commands."
        });
      }
      
      this.terminalInput = "";
      
      // Scroll to bottom
      setTimeout(() => {
        const terminal = document.querySelector('.terminal-body');
        if (terminal) {
          terminal.scrollTop = terminal.scrollHeight;
        }
      }, 100);
    }
  },
};
</script>

<style scoped>
/* Base Structure */
.nic-universe {
  background: radial-gradient(ellipse at center, #0a0f2b 0%, #000000 100%);
  color: white;
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
  font-family: 'Segoe UI', Roboto, sans-serif;
}

.background-elements {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
  pointer-events: none;
}

.content-layer {
  position: relative;
  z-index: 2;
  padding-top: 20px;
}

/* Hero Section */
.hero-section {
  padding: 40px 0 20px;
  min-height: 40vh;
  display: flex;
  align-items: center;
}

.binary-text {
  font-family: 'Courier New', monospace;
  color: rgba(0, 188, 212, 0.2);
  font-size: 0.7rem;
  letter-spacing: 1px;
  margin-bottom: 1rem;
  animation: float 20s infinite linear;
  white-space: nowrap;
  overflow: hidden;
}

@keyframes float {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.logo-text {
  font-family: 'Courier New', monospace;
  color: #00BCD4;
  font-size: 1.2rem;
  letter-spacing: 3px;
  opacity: 0.8;
}

.title-container {
  margin-bottom: 2rem;
  position: relative;
  z-index: 10;
}

.main-title {
  display: flex;
  flex-direction: column;
  gap: 0;
  line-height: 1;
  margin-top: 10px;
}

.title-line {
  display: block;
}

.line-1 {
  color: rgba(255, 255, 255, 0.9);
  font-size: 2.5rem !important;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  margin-bottom: 5px;
}

@media (min-width: 960px) {
  .line-1 {
    font-size: 3.5rem !important;
  }
}

.line-2 {
  font-size: 4rem !important;
  margin-top: -10px;
  margin-bottom: 10px;
}

@media (min-width: 960px) {
  .line-2 {
    font-size: 6rem !important;
  }
}

.nic-gradient {
  background: linear-gradient(45deg, #00BCD4, #3D5AFE);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.scan-line {
  height: 2px;
  background: linear-gradient(90deg, transparent, #00BCD4, transparent);
  margin: 2rem auto;
  width: 300px;
  animation: scan 3s infinite linear;
}

@keyframes scan {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.typing-container {
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
}

.neon-text {
  color: #00BCD4;
  font-family: 'Courier New', monospace;
  font-size: 1.1rem !important;
  line-height: 1.6;
}

.typing-text {
  display: inline-block;
  min-height: 24px;
}

.cursor {
  animation: blink 1s infinite;
  margin-left: 2px;
}

.cursor.typing {
  animation: none;
  opacity: 1;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* Separator Spacing */
.section-spacer {
  height: 80px;
  width: 100%;
}

/* Orbital Section */
.orbital-section {
  position: relative;
  padding: 40px 0 100px;
  margin-top: 20px;
}

.section-title {
  color: white;
  font-size: 2.5rem !important;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  z-index: 5;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}

@media (max-width: 960px) {
  .section-title {
    font-size: 2rem !important;
    margin-bottom: 40px !important;
  }
}

.orbital-container {
  position: relative;
  min-height: 600px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
}

.orbital-system {
  position: relative;
  width: 100%;
  max-width: 800px;
  height: 600px;
  margin: 0 auto;
}

/* Core Styling */
.core {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 120px;
  cursor: pointer;
  z-index: 10;
  text-align: center;
  transition: all 0.3s ease;
}

.core:hover {
  transform: translate(-50%, -50%) scale(1.1);
}

.core-inner {
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(0, 188, 212, 0.5);
  box-shadow: 0 0 50px rgba(0, 188, 212, 0.3);
  transition: all 0.3s ease;
}

.core-pulse {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid rgba(0, 188, 212, 0.2);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
}

.core-label {
  margin-top: 10px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 1px;
  font-family: 'Courier New', monospace;
}

/* Orbital Values */
.orbital-value {
  position: absolute;
  top: calc(50% - 80px);
  left: calc(50% - 80px);
  transform: 
    translate(-50%, -50%)
    rotate(var(--orbit-angle))
    translateX(var(--orbit-radius))
    rotate(calc(-1 * var(--orbit-angle)));
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 5;
}

.orbit-item {
  width: 160px;
  height: 160px;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(var(--orbit-color, 0, 188, 212), 0.3);
  border-radius: 50%;
  padding: 15px;
  cursor: pointer;
  transition: all 0.4s ease;
  position: relative;
  overflow: visible;
}

.orbit-item:hover {
  transform: scale(1.15);
  box-shadow: 0 0 40px rgba(var(--orbit-color, 0, 188, 212), 0.4);
  border-color: rgba(var(--orbit-color, 0, 188, 212), 0.8);
  z-index: 20;
}

.orbit-item.active {
  transform: scale(1.2);
  z-index: 30;
}

.orbit-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(var(--orbit-color, 0, 188, 212), 0.2) 0%, transparent 70%);
  filter: blur(20px);
  z-index: -1;
}

.orb-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;
}

.orb-icon {
  background: rgba(255, 255, 255, 0.1);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid currentColor;
  transition: all 0.3s ease;
  margin-bottom: 10px;
}

.orbit-item:hover .orb-icon {
  transform: rotateY(180deg) scale(1.1);
}

.orb-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin-top: 8px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.orb-tooltip {
  position: absolute;
  top: calc(100% + 15px);
  left: 50%;
  transform: translateX(-50%);
  width: 280px;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(var(--orbit-color, 0, 188, 212), 0.5);
  border-radius: 15px;
  padding: 20px;
  z-index: 100;
  animation: fadeInUp 0.3s ease;
  pointer-events: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.orb-title {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 8px;
  color: white;
}

.orb-description {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
}

.orb-indicator {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 16px;
  height: 16px;
  background: rgba(var(--orbit-color, 0, 188, 212), 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.indicator-dot {
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
}

/* Connection Lines */
.connection-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.connection-line {
  stroke: rgba(255, 255, 255, 0.1);
  stroke-width: 1;
  transition: all 0.3s ease;
}

.connection-line.active {
  stroke: rgba(0, 188, 212, 0.5);
  stroke-width: 2;
  filter: drop-shadow(0 0 5px rgba(0, 188, 212, 0.5));
}

/* Modal Styling */
.value-modal {
  z-index: 1000 !important;
}

.modal-card {
  background: #0a0f2b !important;
  color: white !important;
  border: 1px solid rgba(0, 188, 212, 0.3) !important;
  border-radius: 20px !important;
  overflow: hidden;
}

.modal-header {
  padding: 30px;
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  overflow: hidden;
}

.modal-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 3px solid white;
}

.modal-title {
  flex: 1;
}

.close-btn {
  position: absolute !important;
  top: 15px;
  right: 15px;
}

.tech-tags {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.tech-tag {
  background: rgba(255, 255, 255, 0.1);
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.modal-content {
  padding: 30px !important;
}

.code-preview {
  background: #1e1e1e;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.code-header {
  background: #2d2d2d;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.code-dots {
  display: flex;
  gap: 6px;
}

.code-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.code-dot.red { background: #ff5f56; }
.code-dot.yellow { background: #ffbd2e; }
.code-dot.green { background: #27c93f; }

.code-title {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.code-preview pre {
  margin: 0;
  padding: 20px;
  overflow-x: auto;
}

.code-preview code {
  color: #569cd6;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
}

.metrics-section {
  margin-top: 30px;
}

.metric-card {
  text-align: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.metric-value {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 5px;
}

.metric-label {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 15px;
}

.metric-bar {
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.metric-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 1s ease;
}

.modal-actions {
  padding: 20px 30px !important;
  background: rgba(0, 0, 0, 0.3) !important;
}

/* Stats Section */
.stats-section {
  margin: 80px 0;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  max-width: 1000px;
  margin: 0 auto;
}

.stat-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-10px);
  border-color: currentColor;
}

.stat-card.active {
  background: rgba(255, 255, 255, 0.1);
}

.stat-content {
  position: relative;
  z-index: 2;
}

.stat-value {
  font-size: 3rem;
  font-weight: 900;
  margin-bottom: 10px;
  transition: all 0.3s ease;
}

.stat-card:hover .stat-value {
  transform: scale(1.1);
}

.stat-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
}

.stat-glow {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.stat-card:hover .stat-glow {
  transform: scaleX(1);
}

/* Terminal Section */
.terminal-section {
  margin: 80px 0;
}

.terminal-wrapper {
  max-width: 800px;
  margin: 0 auto;
}

.terminal-container {
  background: rgba(0, 0, 0, 0.9);
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid rgba(0, 188, 212, 0.3);
  box-shadow: 0 0 50px rgba(0, 188, 212, 0.2);
}

.terminal-header {
  background: rgba(0, 0, 0, 0.8);
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.terminal-dots {
  display: flex;
  gap: 8px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.dot.red { background: #ff5f56; }
.dot.yellow { background: #ffbd2e; }
.dot.green { background: #27c93f; }

.terminal-title {
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
}

.terminal-body {
  padding: 20px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: #00ff00;
  min-height: 200px;
  max-height: 300px;
  overflow-y: auto;
}

.terminal-line {
  margin: 10px 0;
}

.prompt {
  color: #00BCD4;
  margin-right: 10px;
}

.command {
  color: white;
}

.output {
  color: #8be9fd;
  margin-left: 20px;
  margin-top: 5px;
  word-break: break-word;
}

.terminal-input {
  display: flex;
  align-items: center;
  margin-top: 20px;
}

.terminal-field {
  background: transparent;
  border: none;
  color: white;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  padding: 5px 10px;
  flex: 1;
  outline: none;
}

.terminal-field::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.terminal-help {
  margin-top: 20px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.terminal-help h4 {
  color: #00BCD4;
  margin-bottom: 10px;
}

.commands-list {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.command-item {
  background: rgba(0, 188, 212, 0.2);
  padding: 5px 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  border: 1px solid rgba(0, 188, 212, 0.5);
  font-family: 'Courier New', monospace;
}

/* CTA Section */
.cta-section {
  margin: 80px 0;
}

.cta-button {
  background: linear-gradient(45deg, #00BCD4, #3D5AFE) !important;
  color: white !important;
  border: none;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease !important;
}

.cta-button:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 188, 212, 0.4) !important;
}

.button-text {
  font-weight: 700;
  letter-spacing: 1px;
  font-size: 1rem;
}

/* Background Elements */
.gradient-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.08;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(45deg, #00BCD4, #3D5AFE);
  top: 20%;
  right: -100px;
}

.orb-2 {
  width: 300px;
  height: 300px;
  background: linear-gradient(45deg, #FF4757, #9C27B0);
  bottom: 20%;
  left: -100px;
}

.floating-nodes {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
}

.node {
  position: absolute;
  background: rgba(0, 188, 212, 0.05);
  border-radius: 50%;
  animation: floatNode 20s infinite linear;
}

@keyframes floatNode {
  0% { transform: translateY(100vh) rotate(0deg); opacity: 0; }
  10% { opacity: 0.1; }
  90% { opacity: 0.1; }
  100% { transform: translateY(-100px) rotate(360deg); opacity: 0; }
}

.hexagon-grid {
  position: fixed;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(circle at 50% 50%, rgba(0, 188, 212, 0.03) 1px, transparent 1px);
  background-size: 100px 100px;
  mask-image: radial-gradient(circle at 50% 50%, black 30%, transparent 70%);
}

/* Responsive Design */
@media (max-width: 960px) {
  .hero-section {
    min-height: 35vh;
    padding: 30px 0 10px;
  }
  
  .section-spacer {
    height: 40px;
  }
  
  .orbital-section {
    padding: 30px 0 60px;
  }
  
  .orbital-container {
    min-height: 500px;
  }
  
  .orbital-system {
    height: 500px;
  }
  
  .orbital-value {
    --orbit-radius: 250px;
    top: calc(50% - 70px);
    left: calc(50% - 70px);
  }
  
  .orbit-item {
    width: 140px;
    height: 140px;
  }
  
  .orb-label {
    font-size: 0.7rem;
  }
  
  .stats-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .hero-section {
    min-height: 30vh;
    padding: 20px 0;
  }
  
  .line-1 {
    font-size: 2rem !important;
  }
  
  .line-2 {
    font-size: 3rem !important;
  }
  
  .neon-text {
    font-size: 1rem !important;
  }
  
  .section-title {
    font-size: 1.5rem !important;
  }
  
  .section-spacer {
    height: 30px;
  }
  
  .orbital-container {
    min-height: 400px;
  }
  
  .orbital-system {
    height: 400px;
  }
  
  .orbital-value {
    --orbit-radius: 180px;
    top: calc(50% - 60px);
    left: calc(50% - 60px);
  }
  
  .orbit-item {
    width: 120px;
    height: 120px;
    padding: 10px;
  }
  
  .orb-icon {
    width: 40px;
    height: 40px;
  }
  
  .orb-label {
    font-size: 0.6rem;
  }
  
  .orb-tooltip {
    width: 200px;
    padding: 15px;
  }
  
  .core {
    width: 80px;
    height: 80px;
  }
  
  .stats-container {
    grid-template-columns: 1fr;
  }
  
  .stat-card {
    padding: 20px;
  }
  
  .stat-value {
    font-size: 2.5rem;
  }
  
  .terminal-container {
    border-radius: 0;
    margin: 0 -12px;
  }
  
  .cta-section {
    margin: 40px 0;
  }
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>